import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./CookieConsent.module.css";

const STORAGE_KEY = "cof_cookie_consent_v1";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function readStoredConsent() {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = safeParse(raw);
  if (!parsed || typeof parsed !== "object") return null;
  if (typeof parsed.analytics !== "boolean" || typeof parsed.marketing !== "boolean") return null;
  return {
    necessary: true,
    analytics: parsed.analytics,
    marketing: parsed.marketing,
    updatedAt: parsed.updatedAt || null,
  };
}

function storeConsent(consent) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      updatedAt: new Date().toISOString(),
    })
  );
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const initial = useMemo(() => readStoredConsent(), []);
  const [consent, setConsent] = useState(
    initial || { necessary: true, analytics: false, marketing: false, updatedAt: null }
  );
  const [hasChoice, setHasChoice] = useState(!!initial);
  const [showSettings, setShowSettings] = useState(false);

  // close settings on Escape
  useEffect(() => {
    if (!showSettings) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowSettings(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showSettings]);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true, updatedAt: new Date().toISOString() };
    setConsent(next);
    storeConsent(next);
    setHasChoice(true);

    window.dispatchEvent(new Event("cookie-consent-updated"));
  };

  const rejectAll = () => {
    const next = { necessary: true, analytics: false, marketing: false, updatedAt: new Date().toISOString() };
    setConsent(next);
    storeConsent(next);
    setHasChoice(true);
  };

  const saveSettings = () => {
    const next = { ...consent, necessary: true, updatedAt: new Date().toISOString() };
    setConsent(next);
    storeConsent(next);
    setHasChoice(true);
    setShowSettings(false);
    window.dispatchEvent(new Event("cookie-consent-updated"));
  };

  if (hasChoice) return null;

  return (
    <>
      <div className={styles.banner} role="region" aria-label={t("cookie_consent_aria")}>
        <div className={styles.inner}>
          <div className={styles.text}>
            <p className={styles.title}>{t("cookie_consent_title")}</p>
            <p className={styles.desc}>{t("cookie_consent_text")}</p>
            <div className={styles.links}>
              <Link className={styles.link} to="/kolacici">
                {t("cookie_consent_link_cookies")}
              </Link>
              <Link className={styles.link} to="/politika-privatnosti">
                {t("cookie_consent_link_privacy")}
              </Link>
              <Link className={styles.link} to="/uslovi-koriscenja">
                {t("cookie_consent_link_terms")}
              </Link>
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={() => setShowSettings(true)}>
              {t("cookie_consent_settings")}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={rejectAll}>
              {t("cookie_consent_reject")}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={acceptAll}>
              {t("cookie_consent_accept")}
            </button>
          </div>
        </div>
      </div>

      {showSettings && (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={t("cookie_settings_title")}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <p className={styles.modalTitle}>{t("cookie_settings_title")}</p>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.row}>
                <div>
                  <p className={styles.rowTitle}>{t("cookie_settings_necessary_title")}</p>
                  <p className={styles.rowDesc}>{t("cookie_settings_necessary_text")}</p>
                </div>
                <div className={styles.toggleWrap}>
                  <span className={styles.pill}>{t("cookie_settings_always_on")}</span>
                </div>
              </div>

              <div className={styles.row}>
                <div>
                  <p className={styles.rowTitle}>{t("cookie_settings_analytics_title")}</p>
                  <p className={styles.rowDesc}>{t("cookie_settings_analytics_text")}</p>
                </div>
                <div className={styles.toggleWrap}>
                  <div
                    className={`${styles.switch} ${consent.analytics ? styles.switchOn : ""}`}
                    onClick={() => setConsent((c) => ({ ...c, analytics: !c.analytics }))}
                    role="switch"
                    aria-checked={consent.analytics}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setConsent((c) => ({ ...c, analytics: !c.analytics }));
                    }}
                  >
                    <div className={`${styles.knob} ${consent.analytics ? styles.knobOn : ""}`} />
                  </div>
                </div>
              </div>

              <div className={styles.row}>
                <div>
                  <p className={styles.rowTitle}>{t("cookie_settings_marketing_title")}</p>
                  <p className={styles.rowDesc}>{t("cookie_settings_marketing_text")}</p>
                </div>
                <div className={styles.toggleWrap}>
                  <div
                    className={`${styles.switch} ${consent.marketing ? styles.switchOn : ""}`}
                    onClick={() => setConsent((c) => ({ ...c, marketing: !c.marketing }))}
                    role="switch"
                    aria-checked={consent.marketing}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setConsent((c) => ({ ...c, marketing: !c.marketing }));
                    }}
                  >
                    <div className={`${styles.knob} ${consent.marketing ? styles.knobOn : ""}`} />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowSettings(false)}>
                {t("cookie_settings_cancel")}
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={saveSettings}>
                {t("cookie_settings_save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

